import { AppLayout } from "@project/components/AppLayout";
import { useAuth } from "@project/contexts/AuthContext";
import { mockInterviews, mockCandidates } from "@project/data/mockData";
import {
  Calendar,
  Clock,
  UserCheck,
  Video,
  Star,
  MessageSquare,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";

const myInterviews = mockInterviews.filter(i => i.interviewerId === "u3");
const today = new Date().toISOString().split("T")[0];

export default function InterviewerDashboard() {
  const { user } = useAuth();

  const upcoming = myInterviews.filter(i => i.status === "Scheduled");
  const completed = myInterviews.filter(i => i.status === "Completed");

  const stats = [
    {
      label: "Today's Interviews",
      value: upcoming.length.toString(),
      icon: Calendar,
      accent: "hsl(var(--primary))",
      bg: "hsl(var(--primary) / 0.08)",
    },
    {
      label: "Completed",
      value: completed.length.toString(),
      icon: CheckCircle2,
      accent: "hsl(160 84% 39%)",
      bg: "hsl(160 84% 39% / 0.08)",
    },
    {
      label: "Pending Feedback",
      value: myInterviews
        .filter(i => i.status === "Completed" && !i.feedback)
        .length.toString(),
      icon: MessageSquare,
      accent: "hsl(38 92% 50%)",
      bg: "hsl(38 92% 50% / 0.08)",
    },
    {
      label: "Total Assigned",
      value: myInterviews.length.toString(),
      icon: UserCheck,
      accent: "hsl(199 89% 48%)",
      bg: "hsl(199 89% 48% / 0.08)",
    },
  ];

  return (
    <AppLayout>
      <div className="mx-auto max-w-7xl space-y-6 p-4 pb-24 sm:pb-6 md:p-6">
        <div>
          <h1 className="font-display text-xl font-bold text-foreground md:text-2xl">
            Welcome, {user?.name}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your interview schedule & feedback dashboard
          </p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-2 gap-3 md:grid-cols-4"
        >
          {stats.map(s => (
            <motion.div
              key={s.label}
              variants={item}
              className="rounded-xl border border-border/60 bg-card p-4"
            >
              <div className="mb-2 flex items-center gap-2">
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-lg"
                  style={{ background: s.bg }}
                >
                  <s.icon className="h-4 w-4" style={{ color: s.accent }} />
                </div>
              </div>
              <p className="font-display text-2xl font-bold text-foreground">
                {s.value}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{s.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Upcoming Interviews */}
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <div className="flex items-center gap-2 border-b border-border/50 px-4 py-3">
            <Calendar className="h-4 w-4 text-primary" />
            <h2 className="font-display text-sm font-semibold text-foreground">
              Upcoming Interviews
            </h2>
          </div>
          <div className="divide-y divide-border/40">
            {upcoming.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No upcoming interviews
              </div>
            ) : (
              upcoming.map(interview => {
                const candidate = mockCandidates.find(
                  c => c.id === interview.candidateId
                );
                return (
                  <div
                    key={interview.id}
                    className="flex items-center gap-4 p-4 transition-colors hover:bg-muted/30"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-xs font-bold text-primary">
                      {interview.candidateAvatar}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground">
                        {interview.candidateName}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {interview.jobTitle} • {interview.round}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="flex items-center gap-1 text-xs font-medium text-foreground">
                        <Clock className="h-3 w-3" /> {interview.time}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {interview.date}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Video className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Completed - Pending Feedback */}
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <div className="flex items-center gap-2 border-b border-border/50 px-4 py-3">
            <MessageSquare className="h-4 w-4 text-warning" />
            <h2 className="font-display text-sm font-semibold text-foreground">
              Completed Interviews
            </h2>
          </div>
          <div className="divide-y divide-border/40">
            {completed.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No completed interviews yet
              </div>
            ) : (
              completed.map(interview => (
                <div
                  key={interview.id}
                  className="flex items-center gap-4 p-4 transition-colors hover:bg-muted/30"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/50 text-xs font-bold text-foreground">
                    {interview.candidateAvatar}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      {interview.candidateName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {interview.jobTitle} • {interview.round}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {interview.feedback?.rating && (
                      <span className="flex items-center gap-0.5 text-xs font-medium text-warning">
                        <Star className="h-3 w-3 fill-warning" />{" "}
                        {interview.feedback.rating}/5
                      </span>
                    )}
                    {interview.feedback ? (
                      <span className="rounded-full bg-accent px-2 py-0.5 text-xs text-accent-foreground">
                        Feedback Given
                      </span>
                    ) : (
                      <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
                        Pending
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
